import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export const CALENDLY_URL = 'https://calendly.com/devyn-thexscale/discovery-call';
export const GROWTHOS_SIGNUP_URL = 'https://smartmarketer.com/growthos/';
const LOGO = '/assets/xscale-logo.png';

// Hero background layer — image URL injected inline to avoid CRA css-loader module resolution.
export const HiggsfieldHero = () => (
  <div
    className="higgsfield-hero"
    aria-hidden="true"
    style={{ '--hero-image': "url('/assets/higgsfield-hero.png')" }}
  />
);

// Cursor glow + noise overlays, shared across pages.
export const SiteEffects = () => {
  const glowRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (glowRef.current) {
        glowRef.current.style.left = `${e.clientX}px`;
        glowRef.current.style.top = `${e.clientY}px`;
      }
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, []);
  return (
    <>
      <div className="noise" aria-hidden="true" />
      <div ref={glowRef} className="cursor-glow" aria-hidden="true" />
    </>
  );
};

// Scroll reveal for .reveal sections. Adds .js to <html> first so the hidden
// reveal state only ever applies when JS is running (no-JS/print safety).
export const useReveal = () => {
  useEffect(() => {
    document.documentElement.classList.add('js');
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        }
      },
      { threshold: 0.14 }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

// Per-page document title + meta description.
export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [title, description]);
};

export const MinimalHeader = () => (
  <header className="site-header" data-testid="minimal-header">
    <Link className="brand" to="/" aria-label="X Scale home" data-testid="header-logo">
      <img src={LOGO} alt="X Scale" />
    </Link>
  </header>
);

export const SiteHeader = ({ onCasePage = false }) => (
  <header className="site-header">
    {onCasePage ? (
      <Link className="brand" to="/" aria-label="X Scale home" data-testid="header-logo">
        <img src={LOGO} alt="X Scale" />
      </Link>
    ) : (
      <a className="brand" href="#top" aria-label="X Scale home" data-testid="header-logo">
        <img src={LOGO} alt="X Scale" />
      </a>
    )}
    <nav className="site-nav" aria-label="Primary navigation">
      <a href={onCasePage ? '/#projects' : '#projects'} data-testid="nav-projects">Projects</a>
      <a href={onCasePage ? '/#thesis' : '#thesis'} data-testid="nav-thesis">Thesis</a>
      <a href={onCasePage ? '/#model' : '#model'} data-testid="nav-model">Model</a>
      <a className="nav-cta" href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="nav-book-call">Book a Call</a>
    </nav>
  </header>
);

export const SiteFooter = ({ onCasePage = false }) => (
  <footer className="site-footer" data-testid="site-footer">
    <img src={LOGO} alt="X Scale" />
    <span>© X Scale</span>
    <span className="footer-tagline">Built systems. Autonomous execution. New scale.</span>
    <nav className="footer-links" aria-label="Footer navigation">
      <a href={onCasePage ? '/#projects' : '#projects'} data-testid="footer-projects">Projects</a>
      <a href={onCasePage ? '/#thesis' : '#thesis'} data-testid="footer-thesis">Thesis</a>
      <Link to="/growthos" data-testid="footer-growthos">GrowthOS</Link>
      <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="footer-contact">Book a Call</a>
    </nav>
  </footer>
);

// GrowthOS Slack demo video with autoplay assist and optional caption.
export const GrowthOSVideo = ({ caption }) => {
  const ref = useRef(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    const tryPlay = () => video.play().catch(() => {});
    video.addEventListener('canplay', tryPlay, { once: true });
    tryPlay();
  }, []);
  return (
    <>
      <div className="growthos-video-card">
        <video
          ref={ref}
          src="/assets/growthos-slack-demo.mp4"
          poster="/assets/growthos-slack-demo-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="GrowthOS Slack conversation with Gary animation"
          data-testid="growthos-demo-video"
        />
      </div>
      {caption && <p className="video-caption" data-testid="growthos-video-caption">{caption}</p>}
    </>
  );
};
